import React, { useState } from 'react'
import { FaUser, FaEnvelope, FaArrowRight, FaUsers, FaComments, FaHeart } from 'react-icons/fa'
import './GuestWelcome.css'

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

const GuestWelcome = ({ 
  sessionName, 
  sessionDescription, 
  userName, 
  userEmail, 
  onUserNameChange, 
  onUserEmailChange, 
  onJoinSession, 
  isLoading = false 
}) => {
  const [errors, setErrors] = useState({})

  const validateForm = () => {
    const newErrors = {}

    if (!userName.trim()) {
      newErrors.userName = 'Tu nombre es necesario para participar'
    } else if (userName.trim().length < 2) {
      newErrors.userName = 'Por favor ingresa un nombre válido'
    }

    if (!userEmail.trim()) {
      newErrors.userEmail = 'Tu email nos ayuda a mantenerte conectado'
    } else if (!EMAIL_REGEX.test(userEmail.trim())) {
      newErrors.userEmail = 'Por favor ingresa un email válido'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (validateForm()) {
      onJoinSession(e)
    }
  }

  const handleNameChange = (e) => {
    const value = e.target.value
    onUserNameChange(e)
    
    // Limpiar error en tiempo real
    if (errors.userName && value.trim().length >= 2) {
      setErrors(prev => ({ ...prev, userName: '' }))
    }
  }

  const handleEmailChange = (e) => {
    const value = e.target.value
    onUserEmailChange(e)
    
    // Limpiar error en tiempo real
    if (errors.userEmail && EMAIL_REGEX.test(value.trim())) {
      setErrors(prev => ({ ...prev, userEmail: '' }))
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-primary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-400 rounded-2xl mb-4 shadow-primary">
            <FaUsers className="text-white text-2xl" />
          </div>
          <h1 className="text-2xl font-bold text-neutral-900 mb-2">
            ¡Bienvenido a la sesión!
          </h1>
          <p className="text-neutral-600 text-sm">
            Te invitamos a ser parte de esta experiencia colaborativa
          </p>
        </div>

        {/* Session Info Card */}
        <div className="bg-white rounded-2xl shadow-soft border border-neutral-100 p-6 mb-6 animate-slide-up">
          <div className="flex items-start gap-3 mb-4">
            <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-primary-100 to-secondary-100 rounded-xl flex items-center justify-center">
              <FaComments className="text-primary-600 text-lg" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold text-neutral-900 text-lg leading-tight mb-1">
                {sessionName}
              </h2>
              {sessionDescription && (
                <p className="text-neutral-600 text-sm leading-relaxed">
                  {sessionDescription}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2 text-xs text-neutral-500 bg-neutral-50 rounded-lg px-3 py-2">
            <FaHeart className="text-primary-400" />
            <span>Un espacio seguro para compartir ideas y colaborar</span>
          </div>
        </div>

        {/* Join Form */}
        <div className="bg-white rounded-2xl shadow-soft border border-neutral-100 p-6 animate-slide-up" style={{ animationDelay: '0.1s' }}>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="text-center mb-6">
              <h3 className="font-semibold text-neutral-900 mb-2">
                Cuéntanos sobre ti
              </h3>
              <p className="text-sm text-neutral-600">
                Solo necesitamos algunos datos para comenzar
              </p>
            </div>

            {/* Name Field */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-neutral-700">
                <div className="flex items-center gap-2 mb-2">
                  <FaUser className="text-primary-500 text-sm" />
                  <span>¿Cómo te llamas?</span>
                </div>
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={userName}
                  onChange={handleNameChange}
                  placeholder="Tu nombre completo"
                  disabled={isLoading}
                  className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 font-medium placeholder-neutral-400 focus:outline-none focus:ring-0 ${
                    errors.userName
                      ? 'border-red-300 bg-red-50 focus:border-red-400'
                      : 'border-neutral-200 bg-white focus:border-primary-400 focus:bg-primary-50/30'
                  } ${isLoading ? 'opacity-60 cursor-not-allowed' : 'hover:border-neutral-300'}`}
                />
              </div>
              {errors.userName && (
                <p className="text-red-500 text-xs flex items-center gap-1 mt-1">
                  <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                  {errors.userName}
                </p>
              )}
            </div>

            {/* Email Field */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-neutral-700">
                <div className="flex items-center gap-2 mb-2">
                  <FaEnvelope className="text-primary-500 text-sm" />
                  <span>Tu email</span>
                </div>
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={userEmail}
                  onChange={handleEmailChange}
                  placeholder="tu@email.com"
                  disabled={isLoading}
                  className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 font-medium placeholder-neutral-400 focus:outline-none focus:ring-0 ${
                    errors.userEmail
                      ? 'border-red-300 bg-red-50 focus:border-red-400'
                      : 'border-neutral-200 bg-white focus:border-primary-400 focus:bg-primary-50/30'
                  } ${isLoading ? 'opacity-60 cursor-not-allowed' : 'hover:border-neutral-300'}`}
                />
              </div>
              {errors.userEmail && (
                <p className="text-red-500 text-xs flex items-center gap-1 mt-1">
                  <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                  {errors.userEmail}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-primary-500 to-secondary-400 hover:from-primary-600 hover:to-secondary-500 disabled:from-neutral-300 disabled:to-neutral-400 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 flex items-center justify-center gap-3 shadow-primary hover:shadow-lg hover:-translate-y-0.5 disabled:hover:translate-y-0 disabled:hover:shadow-primary disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Uniéndote...</span>
                </>
              ) : (
                <>
                  <span>Unirme a la sesión</span>
                  <FaArrowRight className="text-sm" />
                </>
              )}
            </button>
          </form>

          {/* Trust Message */}
          <div className="mt-6 pt-4 border-t border-neutral-100">
            <p className="text-xs text-neutral-500 text-center leading-relaxed">
              Tu información está segura con nosotros. Solo la usamos para mejorar tu experiencia en la sesión.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <p className="text-xs text-neutral-400">
            Powered by <span className="font-semibold text-primary-500">Alleanza</span>
          </p>
        </div>
      </div>
    </div>
  )
}

export default GuestWelcome
